import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  FileText, Plus, ArrowLeft, Clock, Search, Filter, Grid3X3, 
  List, MoreVertical, Play, Edit, Trash2, Copy, Download,
  FolderOpen, Calendar, Layers, ChevronDown, X, Eye, Zap
} from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';
import { confirmToast } from '@/utils/confirmToast';

const TemplatesPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'name', 'most_used'
  const [filterCategory, setFilterCategory] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [stats, setStats] = useState({ total: 0, thisMonth: 0, totalRuns: 0 });

  const fetchTemplates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/templates`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
        
        // Calculate stats
        const now = new Date();
        const thisMonth = data.filter(t => {
          const created = new Date(t.created_at);
          return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
        }).length;
        
        setStats({
          total: data.length,
          thisMonth,
          totalRuns: data.reduce((sum, t) => sum + (t.run_count || 0), 0)
        });
      }
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleDeleteTemplate = (templateId, templateName, e) => {
    e.stopPropagation();
    setActiveMenu(null);
    
    confirmToast(
      `Are you sure you want to delete "${templateName}"?`,
      async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API}/templates/${templateId}`, {
            method: 'DELETE',
            credentials: 'include',
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (response.ok) {
            toast.success('Template deleted');
            fetchTemplates();
          } else {
            throw new Error('Failed to delete');
          }
        } catch (error) {
          toast.error('Failed to delete template');
        }
      },
      { title: 'Delete Template' }
    );
  };

  const handleDuplicateTemplate = async (templateId, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/templates/${templateId}/clone`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Template duplicated');
        fetchTemplates();
      } else {
        throw new Error('Failed to duplicate');
      }
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
    setActiveMenu(null);
  };

  // Filter and sort templates
  const filteredTemplates = templates
    .filter(t => {
      const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           (t.description || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = filterCategory === 'all' || t.category === filterCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'name':
          return a.name.localeCompare(b.name);
        case 'most_used':
          return (b.run_count || 0) - (a.run_count || 0);
        default:
          return 0;
      }
    });

  // Get unique categories
  const categories = ['all', ...new Set(templates.map(t => t.category).filter(Boolean))];

  const formatDate = (dateString) => {
    if (!dateString) return { relative: 'Recently', exact: '' };
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    // Format exact timestamp for tooltip
    const exact = date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    let relative;
    if (diffMins < 1) {
      relative = 'Just now';
    } else if (diffMins < 60) {
      relative = diffMins === 1 ? 'A minute ago' : `${diffMins} minutes ago`;
    } else if (diffHours < 24) {
      relative = diffHours === 1 ? 'About an hour ago' : `About ${diffHours} hours ago`;
    } else if (diffDays === 1) {
      relative = 'Yesterday';
    } else if (diffDays < 7) {
      relative = `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      relative = weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
    } else {
      relative = exact;
    }
    
    return { relative, exact };
  };

  const getCategoryColor = (category) => {
    const colors = {
      contracts: 'bg-blue-100 text-blue-700',
      invoices: 'bg-green-100 text-green-700',
      hr: 'bg-amber-100 text-amber-700',
      legal: 'bg-red-100 text-red-700',
      marketing: 'bg-purple-100 text-purple-700',
    };
    return colors[category] || 'bg-slate-100 text-slate-600';
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50/30 overflow-hidden">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col lg:ml-64">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:pt-8 pt-20">{/* pt-20 for mobile header */}

        {/* Header with Stats */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Templates</h1>
              <p className="text-slate-500">Create, manage, and run your document templates</p>
            </div>
            
            {/* Quick Stats */}
            {templates.length > 0 && (
              <div className="flex gap-4">
                <div className="px-4 py-3 rounded-xl bg-white border border-slate-200 shadow-sm">
                  <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
                  <div className="text-xs text-slate-500">Total Templates</div>
                </div>
                <div className="px-4 py-3 rounded-xl bg-white border border-slate-200 shadow-sm">
                  <div className="text-2xl font-bold text-violet-600">{stats.thisMonth}</div>
                  <div className="text-xs text-slate-500">Created This Month</div>
                </div>
                <div className="px-4 py-3 rounded-xl bg-white border border-slate-200 shadow-sm">
                  <div className="text-2xl font-bold text-green-600">{stats.totalRuns}</div>
                  <div className="text-xs text-slate-500">Total Runs</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mb-4"></div>
            <p className="text-slate-500">Loading your templates...</p>
          </div>
        ) : templates.length === 0 ? (
          /* Empty State */
          <div className="text-center py-16" data-testid="empty-templates-state">
            <div className="max-w-lg mx-auto">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center mx-auto mb-6 shadow-lg">
                <FileText className="w-12 h-12 text-violet-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">Start Creating Templates</h2>
              <p className="text-slate-500 mb-8">
                Upload a Word document with placeholders or a PDF form to get started.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md mx-auto mb-8">
                <button
                  onClick={() => navigate('/templates/builder')}
                  className="p-6 rounded-xl bg-white border-2 border-slate-200 hover:border-blue-400 hover:shadow-lg transition-all text-left group"
                  data-testid="create-docx-template-btn"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">Word Document</h3>
                  <p className="text-sm text-slate-500">Upload .docx with {'{{placeholders}}'}</p>
                </button>
                <button
                  onClick={() => navigate('/templates/pdf-builder')}
                  className="p-6 rounded-xl bg-white border-2 border-slate-200 hover:border-red-400 hover:shadow-lg transition-all text-left group"
                  data-testid="create-pdf-template-btn"
                >
                  <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center mb-4 group-hover:bg-red-200 transition-colors">
                    <FileText className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">PDF Form</h3>
                  <p className="text-sm text-slate-500">AI-powered field extraction</p>
                </button>
              </div>
              
              <div className="flex justify-center">
                <Button
                  onClick={() => navigate('/marketplace')}
                  variant="outline"
                  size="lg"
                  className="h-12 px-8 rounded-xl border-2"
                  data-testid="browse-marketplace-btn"
                >
                  <FolderOpen className="w-5 h-5 mr-2" />
                  Browse Marketplace
                </Button>
              </div>

              {/* Quick Start Guide */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 text-left">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  Quick Start Guide
                </h3>
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
                    <div>
                      <p className="font-medium text-slate-800">Upload your Word document</p>
                      <p className="text-sm text-slate-500">Use placeholders like {'{{company_name}}'} where you want data inserted</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
                    <div>
                      <p className="font-medium text-slate-800">Configure your form fields</p>
                      <p className="text-sm text-slate-500">Set field types, validations, and organize into sections</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
                    <div>
                      <p className="font-medium text-slate-800">Run and export</p>
                      <p className="text-sm text-slate-500">Fill the form and download your completed document</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Search and Filters */}
            <div className="mb-6 space-y-4">
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search templates..."
                    className="pl-10 h-11 rounded-xl border-slate-200"
                    data-testid="search-templates-input"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Filter Toggle */}
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className={`h-11 px-4 rounded-xl ${showFilters ? 'bg-violet-50 border-violet-200 text-violet-700' : ''}`}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                  {(filterCategory !== 'all' || sortBy !== 'newest') && (
                    <span className="ml-2 w-2 h-2 rounded-full bg-violet-600"></span>
                  )}
                </Button>

                {/* Create New Template Button */}
                <Button
                  onClick={() => navigate('/templates/builder')}
                  className="h-11 px-4 rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="create-template-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Template
                </Button>

                {/* View Toggle */}
                <div className="flex rounded-xl border border-slate-200 overflow-hidden">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2.5 ${viewMode === 'grid' ? 'bg-violet-100 text-violet-700' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                    data-testid="grid-view-btn"
                  >
                    <Grid3X3 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2.5 ${viewMode === 'list' ? 'bg-violet-100 text-violet-700' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                    data-testid="list-view-btn"
                  >
                    <List className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Filter Panel */}
              {showFilters && (
                <div className="p-4 rounded-xl bg-white border border-slate-200 flex flex-wrap gap-4">
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">Sort By</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="h-9 rounded-lg border border-slate-200 px-3 text-sm"
                    >
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                      <option value="name">Name (A-Z)</option>
                      <option value="most_used">Most Used</option>
                    </select>
                  </div>
                  {categories.length > 1 && (
                    <div>
                      <label className="text-xs font-medium text-slate-500 mb-1.5 block">Category</label>
                      <select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        className="h-9 rounded-lg border border-slate-200 px-3 text-sm capitalize"
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat} className="capitalize">
                            {cat === 'all' ? 'All Categories' : cat}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="flex items-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSortBy('newest'); setFilterCategory('all'); }}
                      className="text-slate-500"
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Results Count */}
            <div className="mb-4 text-sm text-slate-500">
              {filteredTemplates.length === templates.length 
                ? `${templates.length} template${templates.length !== 1 ? 's' : ''}`
                : `${filteredTemplates.length} of ${templates.length} templates`}
            </div>

            {/* Templates Grid/List */}
            {filteredTemplates.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 font-medium">No templates match your search</p>
                <p className="text-slate-400 text-sm">Try adjusting your filters or search query</p>
              </div>
            ) : viewMode === 'grid' ? (
              /* Grid View */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredTemplates.map((template) => (
                  <div
                    key={template.template_id}
                    className="group relative bg-white rounded-2xl border border-slate-200 hover:border-violet-300 hover:shadow-xl hover:shadow-violet-500/10 transition-all duration-300 overflow-hidden"
                    data-testid={`template-card-${template.template_id}`}
                  >
                    {/* Card Header */}
                    <div className="p-5 pb-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <FileText className="w-5 h-5 text-violet-600" />
                        </div>
                        <div className="flex items-center gap-2">
                          {template.category && (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getCategoryColor(template.category)}`}>
                              {template.category}
                            </span>
                          )}
                          <div className="relative">
                            <button
                              onClick={(e) => { e.stopPropagation(); setActiveMenu(activeMenu === template.template_id ? null : template.template_id); }}
                              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                            {activeMenu === template.template_id && (
                              <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl border border-slate-200 shadow-xl py-1 z-10">
                                <button
                                  onClick={(e) => { e.stopPropagation(); navigate(`/templates/${template.template_id}/run`); setActiveMenu(null); }}
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                                >
                                  <Play className="w-4 h-4 text-green-600" />
                                  Run Template
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); navigate(`/templates/${template.template_id}/edit`); setActiveMenu(null); }}
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                                >
                                  <Edit className="w-4 h-4 text-slate-500" />
                                  Edit Template
                                </button>
                                <button
                                  onClick={(e) => handleDuplicateTemplate(template.template_id, e)}
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                                >
                                  <Copy className="w-4 h-4 text-slate-500" />
                                  Duplicate
                                </button>
                                <div className="h-px bg-slate-100 my-1" />
                                <button
                                  onClick={(e) => handleDeleteTemplate(template.template_id, template.name, e)}
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2 text-red-600"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 mb-1">
                        <h3 
                          className="font-semibold text-slate-900 group-hover:text-violet-700 transition-colors cursor-pointer line-clamp-1 flex-1"
                          onClick={() => navigate(`/templates/${template.template_id}/edit`)}
                        >
                          {template.name}
                        </h3>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          template.template_type === 'pdf' 
                            ? 'bg-red-100 text-red-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {template.template_type === 'pdf' ? 'PDF' : 'DOCX'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 line-clamp-2 min-h-[40px]">
                        {template.description || 'No description'}
                      </p>
                    </div>

                    {/* Card Footer */}
                    <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Layers className="w-3.5 h-3.5" />
                          {template.fields?.length || 0} fields
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          <span title={formatDate(template.created_at).exact}>
                            {formatDate(template.created_at).relative}
                          </span>
                        </span>
                      </div>
                      <Button
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); navigate(`/templates/${template.template_id}/run`); }}
                        className="h-8 px-3 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium"
                        data-testid={`run-template-${template.template_id}`}
                      >
                        <Play className="w-3 h-3 mr-1" />
                        Run
                      </Button>
                    </div>
                  </div>
                ))}

                {/* Create New Card */}
                <div
                  onClick={() => navigate('/templates/builder')}
                  className="group flex flex-col items-center justify-center p-8 rounded-2xl border-2 border-dashed border-slate-200 hover:border-violet-400 hover:bg-violet-50/50 transition-all cursor-pointer min-h-[220px]"
                  data-testid="create-new-template-card"
                >
                  <div className="w-12 h-12 rounded-xl bg-slate-100 group-hover:bg-violet-100 flex items-center justify-center mb-3 transition-colors">
                    <Plus className="w-6 h-6 text-slate-400 group-hover:text-violet-600 transition-colors" />
                  </div>
                  <span className="font-medium text-slate-500 group-hover:text-violet-700 transition-colors">Create New Template</span>
                </div>
              </div>
            ) : (
              /* List View */
              <div className="space-y-4">
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Template</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Category</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Fields</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Created</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredTemplates.map((template) => (
                      <tr 
                        key={template.template_id}
                        className="hover:bg-slate-50 transition-colors"
                        data-testid={`template-row-${template.template_id}`}
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                              <FileText className="w-5 h-5 text-violet-600" />
                            </div>
                            <div className="min-w-0">
                              <p 
                                className="font-medium text-slate-900 hover:text-violet-700 cursor-pointer truncate"
                                onClick={() => navigate(`/templates/${template.template_id}/edit`)}
                              >
                                {template.name}
                              </p>
                              <p className="text-sm text-slate-500 truncate max-w-xs">
                                {template.description || 'No description'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 hidden md:table-cell">
                          {template.category ? (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getCategoryColor(template.category)}`}>
                              {template.category}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-sm">—</span>
                          )}
                        </td>
                        <td className="px-5 py-4 hidden lg:table-cell">
                          <span className="text-sm text-slate-600">{template.fields?.length || 0}</span>
                        </td>
                        <td className="px-5 py-4 hidden sm:table-cell">
                          <span 
                            className="text-sm text-slate-500 cursor-default" 
                            title={formatDate(template.created_at).exact}
                          >
                            {formatDate(template.created_at).relative}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              onClick={() => navigate(`/templates/${template.template_id}/run`)}
                              className="h-8 px-3 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs"
                            >
                              <Play className="w-3 h-3 mr-1" />
                              Run
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/templates/${template.template_id}/edit`)}
                              className="h-8 px-3 rounded-lg text-xs"
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => handleDuplicateTemplate(template.template_id, e)}
                              className="h-8 px-3 rounded-lg text-xs"
                            >
                              <Copy className="w-3 h-3 mr-1" />
                              Duplicate
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => handleDeleteTemplate(template.template_id, template.name, e)}
                              className="h-8 px-3 rounded-lg text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </div>
            )}
          </>
        )}

      {/* Click outside to close menu */}
      {activeMenu && (
        <div className="fixed inset-0 z-0" onClick={() => setActiveMenu(null)} />
      )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatesPage;
